import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useUser } from "@auth0/nextjs-auth0/client";
import { LogoWithGlobe } from "@icons/logos";
import { CloseX } from "@icons/closeX";
import { ImgixNextImage } from "@components/imgixNextImage";
import { ArrowRight } from "@icons/arrowRight";
import { tagular } from "@cohesion/tagular";
import { preventLayoutShift } from "./utils";
import { ProfilePanel } from "./profilePanel";
import { SearchPanel } from "./searchPanel";
import { navigationData } from "./data";

import MobileNavComponent from "./mobileNav";

// Helper to compute initial activeSection state
function getInitialActiveSection(navData) {
  return navData.items.reduce((acc, item) => {
    if (item.submenu?.items.length > 0) {
      acc[item.title] = "0";
    }
    return acc;
  }, {});
}

export function GlobalNav({ disableAlert = false, disableNav = false }) {
  const { user, isLoading } = useUser();
  const hasSession = !!user && !isLoading;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [activeSection, setActiveSection] = useState(() =>
    getInitialActiveSection(navigationData)
  );
  const [alertDismissed, setAlertDismissed] = useState(disableAlert);

  // Check for alert dismissed only if not disabled
  useEffect(() => {
    // Only add event listener if alert is not disabled
    if (!disableAlert) {
      // Add listener for alert dismissal events
      const handleAlertDismissed = () => {
        setAlertDismissed(true);
      };
      document.addEventListener("alertDismissed", handleAlertDismissed);
      return () => {
        document.removeEventListener("alertDismissed", handleAlertDismissed);
      };
    }

    // Return an empty cleanup function if disableAlert is true
    return () => {};
  }, [disableAlert]);

  // Add event listener for search panel toggle
  useEffect(() => {
    // Handler for closing mobile menu when search panel is toggled
    const handleCloseMobileMenu = () => {
      // Using the setter form of setState to avoid dependency on mobileMenuOpen
      setMobileMenuOpen(current => {
        if (current) {
          return false;
        }
        return current;
      });
    };

    // Add event listener
    document.addEventListener("closeMobileMenu", handleCloseMobileMenu);

    // Clean up function
    return () => {
      document.removeEventListener("closeMobileMenu", handleCloseMobileMenu);
    };
  }, []);

  // Handle body overflow and adjust padding when submenu is active
  useEffect(() => {
    // Prevent scrolling when a menu is active
    preventLayoutShift(activeMenu !== null);

    // Clean up function to reset overflow and padding
    return () => {
      preventLayoutShift(false);
    };
  }, [activeMenu]); // Re-run the effect if activeMenu changes

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    document.body.style.overflow = !mobileMenuOpen ? "hidden" : "";

    // Track mobile menu toggle
    tagular("click", {
      actionOutcome: !mobileMenuOpen ? "OPEN" : "CLOSE",
      outboundUrl: "null",
      webElement: {
        location: "GLOBAL NAV",
        elementType: "BUTTON",
        position: "MOBILE MENU",
        text: !mobileMenuOpen ? "OPEN MENU" : "CLOSE MENU",
        name: "GLOBAL NAV HAMBURGER",
      },
    });
  };

  // Handle submenu section click
  const handleSectionClick = (menuTitle, sectionIndex) => {
    setActiveSection(prev => ({
      ...prev,
      [menuTitle]: sectionIndex,
    }));

    // Track submenu section click
    const sectionData = navigationData?.items.find(
      item => item.title === menuTitle
    );
    const sectionTitle =
      sectionData?.submenu?.items[parseInt(sectionIndex, 10)]?.title ||
      "Unknown";

    tagular("click", {
      actionOutcome: "OPEN",
      outboundUrl: "null",
      webElement: {
        location: `SUB NAV - ${menuTitle.toUpperCase()}`,
        elementType: "TAB",
        position: String(parseInt(sectionIndex, 10) + 1),
        text: sectionTitle.toUpperCase(),
      },
    });
  };

  // Render mobile hamburger menu icon
  const renderMobileMenuButton = () => (
    <button
      type="button"
      className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5-400 relative w-10 h-10"
      id="mobile-menu-button"
      aria-expanded={mobileMenuOpen}
      aria-controls="mobile-menu"
      onClick={toggleMobileMenu}
    >
      <span className="sr-only">Open main menu</span>
      <svg
        width="20"
        height="20"
        className={`absolute hamburger-icon ${mobileMenuOpen ? "hidden" : "block"}`}
        viewBox="0 0 14 12"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path d="M1 11.25H13C13.1989 11.25 13.3897 11.171 13.5303 11.0303C13.671 10.8897 13.75 10.6989 13.75 10.5C13.75 10.3011 13.671 10.1103 13.5303 9.96967C13.3897 9.82902 13.1989 9.75 13 9.75H1C0.801088 9.75 0.610323 9.82902 0.46967 9.96967C0.329018 10.1103 0.25 10.3011 0.25 10.5C0.25 10.6989 0.329018 10.8897 0.46967 11.0303C0.610323 11.171 0.801088 11.25 1 11.25Z" />
        <path d="M1 6.75H11C11.1989 6.75 11.3897 6.67098 11.5303 6.53033C11.671 6.38968 11.75 6.19891 11.75 6C11.75 5.80109 11.671 5.61032 11.5303 5.46967C11.3897 5.32902 11.1989 5.25 11 5.25H1C0.801088 5.25 0.610323 5.32902 0.46967 5.46967C0.329018 5.61032 0.25 5.80109 0.25 6C0.25 6.19891 0.329018 6.38968 0.46967 6.53033C0.610323 6.67098 0.801088 6.75 1 6.75Z" />
        <path d="M13 0.75H1C0.801088 0.75 0.610323 0.829018 0.46967 0.96967C0.329018 1.11032 0.25 1.30109 0.25 1.5C0.25 1.69891 0.329018 1.88968 0.46967 2.03033C0.610323 2.17098 0.801088 2.25 1 2.25H13C13.1989 2.25 13.3897 2.17098 13.5303 2.03033C13.671 1.88968 13.75 1.69891 13.75 1.5C13.75 1.30109 13.671 1.11032 13.5303 0.96967C13.3897 0.829018 13.1989 0.75 13 0.75Z" />
      </svg>
      <CloseX
        width="20"
        height="20"
        className={`absolute ${mobileMenuOpen ? "block" : "hidden"}`}
      />
    </button>
  );

  if (disableNav) {
    return null;
  }

  return (
    <>
      <header className="relative z-50 border-b border-black-200 bg-black-100">
        <nav
          className="container flex items-center justify-between h-16 px-4 mx-auto lg:justify-end gap-x-4 lg:gap-x-12"
          aria-label="Global Navigation"
        >
          {/* Mobile Search */}
          <div className="lg:hidden">
            <SearchPanel disableAlert={disableAlert} />
          </div>

          {/* Logo */}
          <div className="flex justify-center flex-1 lg:flex-none lg:justify-start">
            <a
              href="https://www.lonelyplanet.com"
              aria-label="Lonely Planet homepage"
              className="flex items-center"
            >
              <span className="sr-only">Lonely Planet</span>
              <LogoWithGlobe
                className="text-5xl"
                primary="#000000"
                accent="#0066CC"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:self-stretch lg:flex-1">
            <div className="flex items-center justify-center flex-1">
              {navigationData?.items.map((navItem, index) => {
                const { title, url, submenu, featured, cta } = navItem;

                return (
                  <div
                    key={`nav-${title}`}
                    className="relative flex group after:content-[''] after:absolute after:top-0 after:left-0 after:right-0 after:h-full after:bg-transparent after:pointer-events-none after:z-[1] hover:after:h-[100vh] hover:after:pointer-events-auto"
                    role="none"
                    onMouseEnter={() => {
                      setActiveMenu(title);
                    }}
                    onMouseLeave={() => {
                      setActiveMenu(null);
                    }}
                  >
                    <button
                      type="button"
                      className="inline-flex items-center h-full px-8 py-2 group focus:outline-none hover:text-blue hover:underline"
                      aria-expanded={activeMenu === title}
                      aria-controls={`submenu-${title}`}
                      aria-haspopup="true"
                    >
                      <a
                        href={url}
                        className="uppercase hover:text-blue focus:outline-none group-hover:text-blue group-hover:underline relative z-[2]"
                        tabIndex="-1"
                        onClick={() => {
                          // Track main nav item click
                          if (navItem.tracking) {
                            tagular("product_click", {
                              actionOutcome:
                                navItem.tracking.actionOutcome ||
                                "INTERNALLINK",
                              outboundUrl: url,
                              product: {
                                brand: navItem.tracking.product?.brand,
                                location: "GLOBAL NAV",
                                name: title.toUpperCase(),
                                position: index + 2,
                                text: title.toUpperCase(),
                              },
                            });
                          } else {
                            tagular("click", {
                              actionOutcome: "INTERNALLINK",
                              outboundUrl: url,
                              webElement: {
                                elementType: "TAB",
                                location: "GLOBAL NAV",
                                name: title.toUpperCase(),
                                position: String(index + 2),
                                text: title.toUpperCase(),
                              },
                            });
                          }
                        }}
                      >
                        {title}
                      </a>
                    </button>

                    <div
                      id={`submenu-${title}`}
                      className={`fixed z-50 invisible opacity-0 group-hover:visible group-hover:opacity-100 bg-white shadow-lg transition-all duration-200 left-1/2 transform -translate-x-1/2 max-w-[1392px] w-[calc(100%-32px)] ${
                        alertDismissed ? "top-20" : "top-[8rem]"
                      }`}
                      role="menu"
                      aria-label={`${title} submenu`}
                    >
                      <div className="w-full">
                        <div className="grid grid-cols-12 min-h-[400px]">
                          {/* Sidebar Navigation */}
                          <div className="flex flex-col h-full col-span-2 bg-black-100">
                            <nav
                              className="px-6 pt-8"
                              role="navigation"
                              aria-label={`${title} submenu navigation`}
                            >
                              <div className="space-y-1">
                                {submenu?.items.map((section, sectionIdx) => (
                                  <button
                                    key={`${title}-section-${section.title}`}
                                    type="button"
                                    className={`w-full text-left relative inline-block py-2 px-4 my-1 transition-transform duration-200 ${
                                      activeSection[title] ===
                                      sectionIdx.toString()
                                        ? "bg-blue-900 rotate-[-1.5deg] text-white"
                                        : ""
                                    }`}
                                    data-section-index={sectionIdx}
                                    data-menu-title={title}
                                    role="menuitem"
                                    aria-controls={`section-${title}-${sectionIdx}`}
                                    onClick={() =>
                                      handleSectionClick(
                                        title,
                                        sectionIdx.toString()
                                      )
                                    }
                                  >
                                    <div
                                      className={`text-sm uppercase ${
                                        activeSection[title] ===
                                        sectionIdx.toString()
                                          ? "text-white"
                                          : "hover:text-blue cursor-pointer"
                                      }`}
                                    >
                                      {section.title}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </nav>
                            {cta && (
                              <div className="px-6 pt-6 pb-8 mt-auto">
                                <a
                                  href={cta.url}
                                  className="text-sm uppercase hover:text-blue"
                                  role="menuitem"
                                  onClick={() => {
                                    // Track click on CTA
                                    if (cta.tracking) {
                                      tagular("product_click", {
                                        actionOutcome:
                                          cta.tracking.actionOutcome ||
                                          "INTERNALLINK",
                                        outboundUrl: cta.url,
                                        product: {
                                          brand: cta.tracking.product?.brand,
                                          location: `SUB NAV - ${title.toUpperCase()}`,
                                          name: cta.title.toUpperCase(),
                                          text: cta.title,
                                        },
                                      });
                                    } else {
                                      tagular("click", {
                                        actionOutcome: "INTERNALLINK",
                                        outboundUrl: cta.url,
                                        webElement: {
                                          elementType: "LINK",
                                          location: `SUB NAV - ${title.toUpperCase()}`,
                                          name: `VIEW ALL ${title.toUpperCase()}`,
                                          text: cta.title.toUpperCase(),
                                        },
                                      });
                                    }
                                  }}
                                >
                                  {cta.title}
                                </a>
                              </div>
                            )}
                          </div>

                          {/* Main Content Area */}
                          <div
                            className={`${
                              submenu?.items.some(
                                section =>
                                  section.items.length === 1 &&
                                  !("columns" in section.items[0])
                              )
                                ? "col-span-10"
                                : "col-span-7"
                            } p-6`}
                          >
                            <div
                              className={`grid grid-cols-1 gap-y-6 ${
                                submenu?.items.some(
                                  section =>
                                    section.items.length === 1 &&
                                    !("columns" in section.items[0])
                                )
                                  ? ""
                                  : "uppercase"
                              }`}
                            >
                              {submenu?.items.map((section, sectionIdx) => (
                                <div
                                  key={`section-content-${title}-${section.title}`}
                                  className="submenu-section"
                                  data-section-index={sectionIdx}
                                  id={`section-${title}-${sectionIdx}`}
                                  style={{
                                    display:
                                      activeSection[title] ===
                                      sectionIdx.toString()
                                        ? "block"
                                        : "none",
                                  }}
                                  role="region"
                                  aria-label={section.title}
                                >
                                  {section.items.length === 1 &&
                                  !("columns" in section.items[0]) ? (
                                    <div className="col-span-full">
                                      <a
                                        href={section.items[0].url}
                                        className="block group/arrow"
                                        role="menuitem"
                                        onClick={() => {
                                          // Track click on featured section item
                                          if (section.items[0].tracking) {
                                            tagular("product_click", {
                                              actionOutcome:
                                                section.items[0].tracking
                                                  .actionOutcome ||
                                                "INTERNALLINK",
                                              outboundUrl: section.items[0].url,
                                              product: {
                                                brand:
                                                  section.items[0].tracking
                                                    .product?.brand,
                                                location: `SUB NAV - ${title.toUpperCase()} - ${section.title.toUpperCase()}`,
                                                name: section.items[0].title.toUpperCase(),
                                                text: section.items[0].title,
                                              },
                                            });
                                          } else {
                                            tagular("click", {
                                              actionOutcome: "INTERNALLINK",
                                              outboundUrl: section.items[0].url,
                                              webElement: {
                                                elementType: "LINK",
                                                location: `SUB NAV - ${title.toUpperCase()} - ${section.title.toUpperCase()}`,
                                                name: section.items[0].title.toUpperCase(),
                                                text: section.items[0].title,
                                              },
                                            });
                                          }
                                        }}
                                      >
                                        <div className="grid grid-cols-2 gap-4">
                                          <div>
                                            {section.items[0].image && (
                                              <ImgixNextImage
                                                src={section.items[0].image}
                                                alt={section.items[0].title}
                                                className="object-cover w-full h-full"
                                                width={550}
                                                height={300}
                                                sizes="(max-width: 768px) 100vw, 550px"
                                                srcSet={`${section.items[0].image} 550w`}
                                              />
                                            )}
                                          </div>
                                          <div className="flex flex-col justify-between gap-y-2">
                                            <div>
                                              <h3 className="mb-2 text-2xl font-semibold uppercase">
                                                {section.items[0].title}
                                              </h3>
                                              {section.items[0].description && (
                                                <p className="mb-4 text-base normal-case">
                                                  {section.items[0].description}
                                                </p>
                                              )}
                                            </div>
                                            {section.items[0].cta?.url && (
                                              <p className="flex items-center justify-between pb-2 mb-4 text-base uppercase border-b border-black-200 group-hover/arrow:border-blue group-hover/arrow:text-blue">
                                                {section.items[0].cta.title}
                                                <ArrowRight className="invisible w-4 h-4 group-hover/arrow:visible" />
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      </a>
                                    </div>
                                  ) : (
                                    <div
                                      className={`grid ${
                                        section.items.some(
                                          sectionItem =>
                                            "columns" in sectionItem
                                        )
                                          ? `grid-cols-${
                                              section.items.find(
                                                sectionItem =>
                                                  "columns" in sectionItem
                                              )?.columns.length || 3
                                            }`
                                          : "grid-cols-3"
                                      } gap-4`}
                                    >
                                      {section.items.map(sectionItem => {
                                        if ("columns" in sectionItem) {
                                          return sectionItem.columns.map(
                                            (column, columnIdx) => (
                                              <div
                                                key={`${title}-column-${
                                                  column.title || columnIdx
                                                }`}
                                              >
                                                {column.title && (
                                                  <p className="mb-4 text-sm font-semibold">
                                                    {column.title}
                                                  </p>
                                                )}
                                                {column.items.map(subItem => (
                                                  <a
                                                    key={`${title}-subitem-${subItem.title}`}
                                                    href={subItem.url}
                                                    className="block pb-2 mb-2 border-b hover:text-blue border-black-200 hover:border-blue group/arrow"
                                                    role="menuitem"
                                                    onClick={() => {
                                                      // Track click on submenu item
                                                      if (subItem.tracking) {
                                                        tagular(
                                                          "product_click",
                                                          {
                                                            actionOutcome:
                                                              subItem.tracking
                                                                .actionOutcome ||
                                                              "INTERNALLINK",
                                                            outboundUrl:
                                                              subItem.url,
                                                            product: {
                                                              brand:
                                                                subItem.tracking
                                                                  .product
                                                                  ?.brand,
                                                              location: `SUB NAV - ${title.toUpperCase()} - ${section.title.toUpperCase()}`,
                                                              name: subItem.title.toUpperCase(),
                                                              position:
                                                                column.items.indexOf(
                                                                  subItem
                                                                ) + 1,
                                                              text: subItem.title,
                                                            },
                                                          }
                                                        );
                                                      } else {
                                                        tagular("click", {
                                                          actionOutcome:
                                                            "INTERNALLINK",
                                                          outboundUrl:
                                                            subItem.url,
                                                          webElement: {
                                                            elementType: "LINK",
                                                            location: `SUB NAV - ${title.toUpperCase()} - ${section.title.toUpperCase()}`,
                                                            name: subItem.title.toUpperCase(),
                                                            position: String(
                                                              column.items.indexOf(
                                                                subItem
                                                              ) + 1
                                                            ),
                                                            text: subItem.title,
                                                          },
                                                        });
                                                      }
                                                    }}
                                                  >
                                                    <span className="flex items-center justify-between text-sm uppercase gap-x-2">
                                                      {subItem.title}
                                                      <ArrowRight className="invisible w-4 h-4 group-hover/arrow:visible" />
                                                    </span>
                                                  </a>
                                                ))}
                                              </div>
                                            )
                                          );
                                        }

                                        return (
                                          <a
                                            key={`${title}-item-${sectionItem.title}`}
                                            href={sectionItem.url}
                                            className="block pb-2 mb-2 border-b border-black-200 hover:text-blue hover:border-blue group/arrow"
                                            role="menuitem"
                                            onClick={() => {
                                              // Track click on section item
                                              if (sectionItem.tracking) {
                                                tagular("product_click", {
                                                  actionOutcome:
                                                    sectionItem.tracking
                                                      ?.actionOutcome ||
                                                    "INTERNALLINK",
                                                  outboundUrl: sectionItem.url,
                                                  product: {
                                                    brand:
                                                      sectionItem.tracking
                                                        .product?.brand,
                                                    location: `SUB NAV - ${title.toUpperCase()} - ${section.title.toUpperCase()}`,
                                                    name: sectionItem.title.toUpperCase(),
                                                    position:
                                                      section.items.indexOf(
                                                        sectionItem
                                                      ) + 1,
                                                    text: sectionItem.title,
                                                  },
                                                });
                                              } else {
                                                tagular("click", {
                                                  actionOutcome: "INTERNALLINK",
                                                  outboundUrl: sectionItem.url,
                                                  webElement: {
                                                    elementType: "LINK",
                                                    location: `SUB NAV - ${title.toUpperCase()} - ${section.title.toUpperCase()}`,
                                                    name: sectionItem.title.toUpperCase(),
                                                    position: String(
                                                      section.items.indexOf(
                                                        sectionItem
                                                      ) + 1
                                                    ),
                                                    text: sectionItem.title,
                                                  },
                                                });
                                              }
                                            }}
                                          >
                                            {sectionItem.image && (
                                              <ImgixNextImage
                                                src={sectionItem.image}
                                                alt={sectionItem.title}
                                                className="object-cover w-full h-40 mb-2"
                                                width={160}
                                                height={160}
                                                sizes="(max-width: 768px) 100vw, 160px"
                                                srcSet={`${sectionItem.image} 160w`}
                                              />
                                            )}
                                            <span className="flex items-center justify-between text-sm uppercase gap-x-2">
                                              {sectionItem.title}
                                              <ArrowRight className="invisible w-4 h-4 group-hover/arrow:visible" />
                                            </span>
                                            {sectionItem.description && (
                                              <p className="my-4 text-sm text-black normal-case">
                                                {sectionItem.description}
                                              </p>
                                            )}
                                          </a>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Featured Content */}
                          {featured &&
                            !submenu?.items.some(
                              section =>
                                section.items.length === 1 &&
                                !("columns" in section.items[0])
                            ) && (
                              <div className="col-span-3 p-6 bg-white border-l border-black-200">
                                <div>
                                  <ImgixNextImage
                                    src={featured.image}
                                    alt=""
                                    className="object-cover w-full h-48 mb-4"
                                    width={350}
                                    height={200}
                                    sizes="(max-width: 768px) 100vw, 350px"
                                    srcSet={`${featured.image} 350w`}
                                  />
                                  <h3 className="mb-2 text-xl">
                                    {featured.title}
                                  </h3>
                                  <p className="mb-4 text-sm">
                                    {featured.description}
                                  </p>
                                  <a
                                    href={featured.cta.url}
                                    className="inline-block w-full px-4 py-2 text-sm text-center text-white uppercase transition-colors bg-black rounded hover:bg-blue"
                                    role="menuitem"
                                    onClick={() => {
                                      // Track click on featured CTA
                                      if (featured.tracking) {
                                        tagular("product_click", {
                                          actionOutcome:
                                            featured.tracking.actionOutcome ||
                                            "INTERNALLINK",
                                          outboundUrl: featured.cta.url,
                                          product: {
                                            brand:
                                              featured.tracking.product?.brand,
                                            location: `SUB NAV - ${title.toUpperCase()}`,
                                            name: featured.cta.title.toUpperCase(),
                                            text: featured.cta.title,
                                          },
                                        });
                                      } else {
                                        tagular("click", {
                                          actionOutcome: "INTERNALLINK",
                                          outboundUrl: featured.cta.url,
                                          webElement: {
                                            elementType: "LINK",
                                            location: `SUB NAV - ${title.toUpperCase()}`,
                                            name: featured.cta.title.toUpperCase(),
                                            text: featured.cta.title,
                                          },
                                        });
                                      }
                                    }}
                                  >
                                    {featured.cta.title}
                                  </a>
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="hidden lg:block">
                <SearchPanel disableAlert={disableAlert} />
              </div>
            </div>
          </div>

          {/* User Actions */}
          <div className="items-center hidden lg:flex gap-x-4 lg:gap-x-6">
            <ProfilePanel
              hasSession={hasSession}
              user={user}
              alertDismissed={alertDismissed}
              disableAlert={disableAlert}
            />
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden">{renderMobileMenuButton()}</div>
        </nav>
      </header>

      {/* Mobile Navigation */}
      <MobileNavComponent
        alertDismissed={alertDismissed}
        isOpen={mobileMenuOpen}
        onClose={toggleMobileMenu}
        hasSession={hasSession}
        disableAlert={disableAlert}
      />
    </>
  );
}

GlobalNav.propTypes = {
  disableNav: PropTypes.bool,
  disableAlert: PropTypes.bool,
};

export default GlobalNav;
